import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { format } from "date-fns";
import { useFormDetails } from "@/features/forms/hooks/use-form";
import { useResyncForm } from "@/features/forms/hooks/use-resync-form";
import { ContentLayout } from "@/components/common/content-layout";
import { HasAccess } from "@/components/common/has-access";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { codeToFlag } from "@/features/regions/utils/code-to-flag";
import { FormQuestion } from "@/pages/forms/types/form";
import {
  PROVIDER_BADGE,
  PROVIDER_LABEL,
} from "@/features/forms/constants";
import FormSubmissionsChart from "@/features/forms/ui/form-submissions-chart";
import FormBundlesCard from "@/features/forms/ui/form-bundles-card";
import FormQuestionsCard from "@/features/forms/ui/form-questions-card";
import FormFieldMappingCard from "@/features/forms/ui/form-field-mapping-card";

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="text-sm text-gray-900 mt-0.5 truncate">{value || "—"}</p>
    </div>
  );
}

export default function FormDetails() {
  const { id, region } = useParams();
  const navigate = useNavigate();
  const { data: form, isLoading, isFetching, error } = useFormDetails(id);
  const resync = useResyncForm(id as string);

  const questions: FormQuestion[] = Array.isArray(form?.questions)
    ? (form?.questions as FormQuestion[])
    : [];

  return (
    <ContentLayout
      data={form ? [form] : []}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      contentClassName="space-y-6"
      errorMessage="Failed to load form"
      emptyState={{ message: "Form not found" }}
    >
      {form && (
        <div className="w-full space-y-6">
          <button
            type="button"
            onClick={() => navigate(`/${region ?? "all"}/forms`)}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" /> Forms
          </button>

          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold text-gray-900">
                {form.name}
              </h1>
              <p className="text-xs font-mono text-gray-400 mt-1 break-all">
                source_id {form.source_id}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge variant="outline" className="text-[10px]">
                {PROVIDER_BADGE[form.provider]}
              </Badge>
              {form.region_rel && (
                <Badge variant="outline" className="text-[10px] uppercase">
                  {codeToFlag(form.region_rel.code)} {form.region_rel.code}
                </Badge>
              )}
              <Badge
                variant={form.status === "ACTIVE" ? "active" : "inactive"}
                className="text-[10px] uppercase"
              >
                {form.status}
              </Badge>
            </div>
          </div>

          {/* Row 1: Overview (metadata + chart) + Active offerings */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            <Card className="lg:col-span-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Overview</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Meta label="Department" value={form.department} />
                  <Meta
                    label="Region"
                    value={
                      form.region_rel
                        ? `${codeToFlag(form.region_rel.code)} ${form.region_rel.display_name}`
                        : "—"
                    }
                  />
                  <Meta
                    label="Provider"
                    value={PROVIDER_LABEL[form.provider]}
                  />
                  <Meta label="Status" value={form.status} />
                  <Meta label="Workspace" value={form.workspace_name} />
                  <Meta
                    label="Doc link"
                    value={
                      form.doc_link ? (
                        <a
                          href={form.doc_link}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          Open ↗
                        </a>
                      ) : (
                        "—"
                      )
                    }
                  />
                  <Meta
                    label="Created"
                    value={format(new Date(form.created_at), "dd MMM yyyy")}
                  />
                  <Meta
                    label="Updated"
                    value={format(new Date(form.updated_at), "dd MMM yyyy")}
                  />
                </div>

                <FormSubmissionsChart formId={form.id} />
              </CardContent>
            </Card>

            <div className="lg:col-span-1 h-full">
              <FormBundlesCard bundles={form.bundles ?? []} />
            </div>
          </div>

          {/* Row 2: Questions + Field mapping (fixed height, scroll, search) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <FormQuestionsCard
              questions={questions}
              provider={form.provider}
              action={
                <HasAccess permission="write">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={resync.isPending}
                    onClick={() => resync.mutate()}
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-1" />
                    {resync.isPending ? "Syncing…" : "Re-fetch"}
                  </Button>
                </HasAccess>
              }
            />
            <FormFieldMappingCard
              formId={form.id}
              questions={questions}
              mapping={form.field_mapping ?? []}
            />
          </div>
        </div>
      )}
    </ContentLayout>
  );
}
